import clsx from "clsx";
import { Check } from "lucide-react";
import React from "react";
import Icon from "components/Icon";
import Image from "components/Image";

export interface TimelineStep {
  title: string;
  completed?: boolean;
  subtitle?: string;
  icon?: string;
  imageSrc?: string;
}

interface TimelineProps {
  title: string;
  steps?: TimelineStep[];
  bgColor?: string;
  showStepNumbers: boolean;
  variant?: "default" | "compact" | "expanded";
  titleColor?: string;
  stepColor?: string;
  lineColor?: string;
  buttonText?: string;
  buttonIcon?: string;
  isBigTitle?: boolean;
  stepsCenterAligned?: boolean;
}

const Timeline: React.FC<TimelineProps> = ({
  title,
  steps,
  showStepNumbers,
  bgColor = "#f0f5ff",
  variant = "default",
  titleColor = "#ffffff",
  stepColor = "#183837",
  lineColor = "#e8eaed",
  buttonText,
  buttonIcon,
  isBigTitle = false,
  stepsCenterAligned = false
}) => {
  if (!steps || steps.length === 0) {
    return <div>No steps available.</div>;
  }

  const getStepSize = () => {
    switch (variant) {
      case "compact":
        return "h-6 w-6";
      case "expanded":
        return "h-12 w-12";
      default:
        return "h-9 w-9";
    }
  };

  const getSpacing = () => {
    switch (variant) {
      case "compact":
        return "pb-4 ";
      case "expanded":
        return "pb-8";
      default:
        return "pb-6";
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm" style={{ backgroundColor: bgColor }}>
      <div className={`py-8 pb-16 px-8 ${variant === "compact" ? "text-sm" : "text-base"}`}>
        <p
          style={{
            color: titleColor
          }}
          className={`pb-6 text-2xl font-bold  lg:pb-10 ${isBigTitle ? "text-balance text-center text-[42px] leading-[48px] lg:text-[52px] lg:leading-[64px]" : ""}`}
        >
          {title}
        </p>
        <ul role="list" className={clsx(stepsCenterAligned && "mx-auto lg:max-w-xl")}>
          {steps.map((step, index) => (
            <li key={index} className={clsx("relative flex gap-x-4")}>
              <div className="h-inherit relative flex items-start justify-center">
                <div className={clsx("absolute inset-0 flex h-full justify-center")}>
                  <div className="w-px" style={{ backgroundColor: lineColor }} />
                </div>
                <div
                  className={`relative flex ${getStepSize()} flex-none items-center justify-center rounded-full ${
                    step.completed ? "bg-primary-700" : "bg-gray-200"
                  }`}
                >
                  {step.completed ? (
                    <Check size={variant === "compact" ? 14 : 18} strokeWidth={2.5} color="#ffffff" />
                  ) : showStepNumbers ? (
                    <span className={`text-sm font-semibold ${stepColor}`}>{index + 1}</span>
                  ) : step.icon ? (
                    <Icon name={step.icon as any} size={variant === "compact" ? 14 : 18} strokeWidth={2.5} />
                  ) : (
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400"></div>
                  )}
                </div>
              </div>
              <div className={clsx(getSpacing(), "flex flex-grow gap-2", index === steps.length - 1 && "!pb-0")}>
                {!!(variant !== "compact" && step.imageSrc) && (
                  <div className="relative h-[80px] w-[80px] md:h-[100px] md:w-[100px]  ">
                    <Image src={step.imageSrc} fill alt={`step_image_${index}`} />
                  </div>
                )}
                <div className="flex-1">
                  <p
                    style={{
                      color: stepColor
                    }}
                    className={clsx(
                      `flex-auto py-0.5 ${
                        variant === "compact" ? "text-sm" : "text-base"
                      } font-bold ${step.completed ? "!text-green-700" : ""}`
                    )}
                  >
                    {step.title}
                  </p>
                  {step.subtitle && variant !== "compact" && (
                    <p className="mt-2 text-sm text-gray-500">{step.subtitle}</p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {!!buttonText && (
          <button className="mt-8 flex items-center rounded-md bg-primary-700 px-4 py-2 text-white">
            {buttonIcon && <Icon name={buttonIcon as any} className="mr-2 h-4 w-4" />}
            {buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default Timeline;